import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, HelpCircle, MessageCircle, Phone, Mail } from 'lucide-react';

// Chat / Call / Email tiles — a tap triggers a real action (dialer,
// mail client, chat stub). We used to have a fourth "FAQ" tile that
// had no handler and scrolled to a FAQs section rendered right below;
// deleted (PR 11 / tester issue #8) to stop farmers hunting for a
// contact method that doesn't do anything.
const SUPPORT_OPTIONS = [
  { icon: MessageCircle, key: 'chat', action: 'chat' },
  { icon: Phone, key: 'call', action: 'call' },
  { icon: Mail, key: 'email', action: 'email' },
];

const FAQ_KEYS = ['addPlot', 'diagnosis', 'subscribe', 'cancel'];

export default function SupportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAction = (action) => {
    switch (action) {
      case 'call':
        window.location.href = 'tel:+2348001234567';
        break;
      case 'email':
        window.location.href = 'mailto:support@farmafrica.com';
        break;
      case 'chat':
        navigate('/support/chat');
        break;
      default:
        break;
    }
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('support.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('support.subtitle')}</p>
        </div>
      </div>

      {/* Support Options */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {SUPPORT_OPTIONS.map((option) => (
          <Card
            key={option.key}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleAction(option.action)}
          >
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-farm-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <option.icon className="w-6 h-6 text-farm-primary" />
              </div>
              <h3 className="font-semibold text-sm">{t(`support.options.${option.key}.title`)}</h3>
              <p className="text-xs text-muted-foreground mt-1">{t(`support.options.${option.key}.description`)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQs */}
      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-farm-primary" />
        {t('support.faqTitle')}
      </h2>
      <div className="space-y-3">
        {FAQ_KEYS.map((key) => (
          <Card key={key}>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2">{t(`support.faqs.${key}.q`)}</h3>
              <p className="text-sm text-muted-foreground">{t(`support.faqs.${key}.a`)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* App Version */}
      <div className="text-center mt-8 text-sm text-muted-foreground">
        <p>{t('support.version')}</p>
        <p className="mt-1">{t('support.copyright')}</p>
      </div>
    </div>
  );
}
